/**
 * JOAdminLoginInjector — cleans up the /admin/login page for Jardins d'Éole:
 *   1. Hides the "Compte support MSC" banner shown in "Accès rapide" mode
 *      (informative for MSC agents, but confusing for the client).
 *   2. Injects 3 quick-login buttons (Admin / Client Pro / Client Retail)
 *      that pre-fill the form and submit it.
 *
 * ENVIRONMENT GATE:
 *   Buttons only render in the Preview environment (any *.preview.emergentagent.com
 *   host, or localhost). They are hidden in production so real users never see them.
 *
 * NON-CORE — additive DOM injector, mounted globally from App.js.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const MSC_BANNER_SELECTOR = '[data-testid="login-quick-banner"]';
const QUICK_ROW_ID = "jo-admin-quick-login-row";

const ACCOUNTS = [
  {
    key: "admin",
    label: "Admin",
    email: "support@agence-msc.fr",
    password: "MSCpass!13!support",
    testId: "jo-quick-login-admin",
    color: "#0f2b4f",
  },
  {
    key: "pro",
    label: "Client Pro",
    email: "propaysage2@test.fr",
    password: "TestPass123!",
    testId: "jo-quick-login-pro",
    color: "#2d6a4f",
  },
  {
    key: "retail",
    label: "Client Retail",
    email: "contact@kloubair.fr",
    password: "TestPass123!",
    testId: "jo-quick-login-retail",
    color: "#a06a2f",
  },
];

function isPreviewEnv() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname || "";
  return (
    host.includes("preview.emergentagent.com") ||
    host === "localhost" ||
    host === "127.0.0.1"
  );
}

/** Fire native setter so React registers the change. */
function setNativeValue(el, value) {
  const proto = Object.getPrototypeOf(el);
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  const parentSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set;
  if (setter && setter !== parentSetter) setter.call(el, value);
  else if (parentSetter) parentSetter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event("input", { bubbles: true }));
  el.dispatchEvent(new Event("change", { bubbles: true }));
}

async function loginAsCustomer(account) {
  // Uses /api/customer/login and stores the customer token, then redirects.
  const api = `${process.env.REACT_APP_BACKEND_URL}/api`;
  try {
    const resp = await fetch(`${api}/customer/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: account.email, password: account.password }),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    if (data?.token) {
      localStorage.setItem("msc_customer_token", data.token);
      window.location.href = "/account";
    } else {
      // Fallback — pre-fill the customer login form
      window.location.href = `/account/login?email=${encodeURIComponent(account.email)}`;
    }
  } catch (e) {
    console.warn("Quick-login customer failed:", e);
    window.location.href = `/account/login?email=${encodeURIComponent(account.email)}`;
  }
}

function fillAndSubmit(account) {
  if (account.key !== "admin") {
    // Client Pro / Retail → hit /api/customer/login, redirect to /account
    loginAsCustomer(account);
    return;
  }
  // Admin flow — use the on-page admin login form
  const fullModeBtn = document.querySelector('[data-testid="login-mode-full"]');
  if (fullModeBtn) fullModeBtn.click();

  // Give React a tick to render the email input
  setTimeout(() => {
    const emailEl = document.querySelector('[data-testid="login-email"]');
    const pwdEl = document.querySelector('[data-testid="login-password"]');
    if (emailEl) setNativeValue(emailEl, account.email);
    if (pwdEl) setNativeValue(pwdEl, account.password);
    setTimeout(() => {
      const submit = document.querySelector('[data-testid="login-submit"]');
      if (submit) submit.click();
    }, 80);
  }, 60);
}

function ensureButtonsInjected() {
  if (document.getElementById(QUICK_ROW_ID)) return true; // already there
  const form = document.querySelector('[data-testid="login-form"]');
  if (!form) return false;

  const wrap = document.createElement("div");
  wrap.id = QUICK_ROW_ID;
  wrap.setAttribute("data-testid", "jo-admin-quick-login-row");
  wrap.style.cssText =
    "margin-top:14px;padding-top:14px;border-top:1px dashed hsl(var(--border));";

  const label = document.createElement("div");
  label.textContent = "Accès rapide (preview uniquement)";
  label.style.cssText =
    "font-size:10px;text-transform:uppercase;letter-spacing:.14em;font-weight:700;color:hsl(var(--muted-foreground));margin-bottom:8px;";
  wrap.appendChild(label);

  const row = document.createElement("div");
  row.style.cssText = "display:grid;grid-template-columns:repeat(3,1fr);gap:8px;";

  ACCOUNTS.forEach((acc) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.setAttribute("data-testid", acc.testId);
    btn.textContent = acc.label;
    btn.style.cssText = `
      height:36px;
      border-radius:9999px;
      font-size:12px;
      font-weight:700;
      color:#fff;
      background:${acc.color};
      border:none;
      cursor:pointer;
      transition:transform .15s, box-shadow .15s, opacity .15s;
    `;
    btn.addEventListener("mouseenter", () => {
      btn.style.transform = "translateY(-1px)";
      btn.style.boxShadow = "0 6px 14px rgba(0,0,0,.15)";
    });
    btn.addEventListener("mouseleave", () => {
      btn.style.transform = "";
      btn.style.boxShadow = "";
    });
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      fillAndSubmit(acc);
    });
    row.appendChild(btn);
  });

  wrap.appendChild(row);
  form.appendChild(wrap);
  return true;
}

function hideMscBanner() {
  const banner = document.querySelector(MSC_BANNER_SELECTOR);
  if (banner && banner.style.display !== "none") {
    banner.style.display = "none";
  }
}

export default function JOAdminLoginInjector() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/admin/login") return;

    const preview = isPreviewEnv();

    const run = () => {
      hideMscBanner();
      if (preview) ensureButtonsInjected();
    };

    run();
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      run();
      if (tries > 20) clearInterval(interval);
    }, 200);

    const observer = new MutationObserver(run);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearInterval(interval);
      observer.disconnect();
      // Cleanup on route change
      const row = document.getElementById(QUICK_ROW_ID);
      if (row) row.remove();
    };
  }, [location.pathname]);

  return null;
}
